import { useEffect } from "react";
import { SlidersHorizontal, X } from "lucide-react";

/**
 * Filtres sur téléphone : un bouton carré à côté de la recherche (avec le nombre de filtres actifs),
 * qui ouvre un panneau par le bas. Sur ordinateur, les pages gardent leurs filtres habituels.
 */
const ACCENTS = {
  violet: { badge: "bg-violet-500 text-white", on: "border-violet-500/50 text-violet-300", btn: "bg-violet-500 text-white" },
  orange: { badge: "bg-orange-400 text-black", on: "border-orange-500/50 text-orange-300", btn: "bg-orange-400 text-black" },
  emerald: { badge: "bg-emerald-500 text-black", on: "border-emerald-500/50 text-emerald-300", btn: "bg-emerald-500 text-black" },
};

export function FilterButton({ count = 0, onClick, accent = "emerald", testid = "filter-button" }) {
  const a = ACCENTS[accent] || ACCENTS.emerald;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={count ? `Filtres (${count} actif${count > 1 ? "s" : ""})` : "Filtres"}
      className={`relative shrink-0 grid place-items-center w-11 h-11 rounded-md border bg-white/[0.03] ${count ? a.on : "border-white/10 text-zinc-300"}`}
      data-testid={testid}
    >
      <SlidersHorizontal className="w-[18px] h-[18px]" />
      {count > 0 && (
        <span className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold grid place-items-center ${a.badge}`}>
          {count}
        </span>
      )}
    </button>
  );
}

/** Une rubrique du panneau : titre + boutons (choix unique). */
export function FilterGroup({ label, options, value, onChange, accent = "emerald" }) {
  const a = ACCENTS[accent] || ACCENTS.emerald;
  return (
    <div className="py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={`h-9 px-3.5 rounded-full border text-sm transition-colors ${value === o.id ? a.on + " bg-white/[0.04]" : "border-white/10 text-zinc-400"}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FilterSheet({ open, onClose, onReset, resultLabel, accent = "emerald", children }) {
  const a = ACCENTS[accent] || ACCENTS.emerald;
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70]" data-testid="filter-sheet">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto overscroll-contain rounded-t-2xl border-t border-white/10 bg-[#121214] px-5 pt-2 animate-in slide-in-from-bottom duration-200"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        role="dialog"
        aria-modal="true"
        aria-label="Filtres"
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between py-2">
          <div className="font-display font-bold text-lg">Filtres</div>
          <button type="button" onClick={onClose} className="p-2 -mr-2 text-zinc-500" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="divide-y divide-white/5">{children}</div>
        <div className="mt-3 flex gap-3">
          <button type="button" onClick={onReset} className="h-11 px-4 rounded-md border border-white/10 text-sm text-zinc-300">
            Réinitialiser
          </button>
          <button type="button" onClick={onClose} className={`flex-1 h-11 rounded-md text-sm font-medium ${a.btn}`} data-testid="filter-sheet-apply">
            {resultLabel || "Voir les résultats"}
          </button>
        </div>
      </div>
    </div>
  );
}
